import { FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { tiposTarefaHooks } from './hooks';
import { tipoTarefaInputSchema, type TipoTarefaFormValues } from './schemas';
import type { TipoTarefaDto } from './types';

export interface TipoTarefaFormDialogProps {
  open: boolean;
  tipoTarefa: TipoTarefaDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: TipoTarefaFormValues = {
  nome: '',
  relacaoHora: null,
  ativo: true,
};

export function TipoTarefaFormDialog({
  open,
  tipoTarefa,
  onClose,
}: TipoTarefaFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    TipoTarefaFormValues,
    TipoTarefaDto,
    TipoTarefaFormValues,
    number
  >({
    open,
    entity: tipoTarefa,
    schema: tipoTarefaInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (tt) => ({ nome: tt.nome, relacaoHora: tt.relacaoHora, ativo: tt.ativo }),
    getId: (tt) => tt.id,
    hooks: { useCreate: tiposTarefaHooks.useCreate, useUpdate: tiposTarefaHooks.useUpdate },
    messages: {
      created: t('features.tiposTarefa.toasts.created'),
      updated: t('features.tiposTarefa.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.tiposTarefa.editTitle') : t('features.tiposTarefa.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.tiposTarefa.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <Controller
          control={form.control}
          name="relacaoHora"
          render={({ field, fieldState }) => (
            <TextField
              fullWidth
              type="number"
              label={t('features.tiposTarefa.fields.relacaoHora')}
              helperText={
                fieldState.error?.message ?? t('features.tiposTarefa.fields.relacaoHoraHelper')
              }
              error={Boolean(fieldState.error)}
              inputProps={{ step: '0.01', min: -99.99, max: 99.99 }}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? null : Number(e.target.value))
              }
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, checked) => field.onChange(checked)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.tiposTarefa.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}
